import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useSidebar } from "../../content/SidebarContent";
import PageHeader from "../../components/PageHeader";
import { reqToGetTenantDetails } from "../../redux/slices.js/tenants/tenantsSlice";
import { reqToGetPlanList } from "../../redux/slices.js/plans/plansSlice";
import { displayDateAsDMY } from "../../helpers/commonFunctions";
import { reqToGetCouponList } from "../../redux/slices.js/promotioncoupons/couponSlice";
import logo from "../../assets/images/logo.svg";
import { IMAGE_URL } from "../../config";

const companyTypeOptions = [
  { value: "Private", label: "Private" },
  { value: "Public", label: "Public" },
  { value: "Startup", label: "Startup" },
];

const teamStrengthOptions = [
  { value: "1-10", label: "1-10" },
  { value: "11-50", label: "11-50" },
  { value: "51-200", label: "51-200" },
  { value: "200+", label: "200+" },
];

const ViewTenant = () => {
  const { setIsSidebarOpen } = useSidebar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const tenant = useSelector((state) => state.tenants.tenantDeatils);
  const plans = useSelector((state) => state.plans.plans);
  const coupons = useSelector((state) => state.promotionCoupons.CouponList);
  const staff = useSelector((state) => state.staff.staffList);

  useEffect(() => {
    dispatch(
      reqToGetTenantDetails({
        id: Number(id),
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
    dispatch(
      reqToGetPlanList({
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
    dispatch(
      reqToGetCouponList({
        data: {
          tab: "private",
          search: "",
          page: 1,
          limit: 100,
        },
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, []);

  // Helper function to get label from value
  const getLabelFromValue = (options, value) => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : "N/A";
  };

  // Get staff name from ID
  const getStaffName = (staffId) => {
    const staffMember = staff.find(s => s.id === staffId);
    return staffMember ? staffMember.name : "N/A";
  };

  // Get coupon name from ID
  const getCouponName = (couponId) => {
    const coupon = coupons.find(c => c.id === couponId);
    return coupon ? coupon.name : "N/A";
  };

  // Get plan name from ID
  const getPlanName = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan ? plan.name : "N/A";
  };

  return (
    <section className="dashboard-page main-layout-page main-layout h-[calc(100vh-50px)]">
      <div className="!p-0">
        <div className="main-layout-wrapper bg-white h-screen flex flex-col sticky z-1 top-0">
          <PageHeader
            title="View Tenant"
            description="View tenant profile details."
            onOpenSidebar={() => setIsSidebarOpen(true)}
          />

          <div className="flex items-baseline gap-[40px] main-sidebar-wrapper flex-1 overflow-y-auto">
            <div className="flex-1 right-sidebar h-full relative">
              <div className="right-sidebar-contend py-6 px-3 md:px-6 h-full overflow-y-auto">
                <div className="flex flex-col gap-6">
                  {/* Company Information Block */}
                  <div className="bg-white box-shadow p-5 space-y-7 rounded-md">
                    <h2 className="text-h4 text-blue border-b pb-2">Company Information</h2>
                    
                    <div className="flex flex-col md:flex-row gap-6 items-start">
                      <div className="flex justify-center items-center p-4 bg-gray-100 rounded-lg">
                        <img
                          src={tenant?.tenant?.logo ? IMAGE_URL + tenant.tenant.logo : logo}
                          alt="Company Logo"
                          className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-md"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1">
                        <div className="info-item">
                          <label className="font-semibold text-gray-700 block mb-1">Company Name</label>
                          <div className="text-gray-900 p-2 bg-gray-50 rounded">{tenant?.tenant?.name || "N/A"}</div>
                        </div>

                        <div className="info-item">
                          <label className="font-semibold text-gray-700 block mb-1">Company Type</label>
                          <div className="text-gray-900 p-2 bg-gray-50 rounded">
                            {getLabelFromValue(companyTypeOptions, tenant?.tenant?.tenant_type)}
                          </div>
                        </div>

                        <div className="info-item">
                          <label className="font-semibold text-gray-700 block mb-1">Team Strength</label>
                          <div className="text-gray-900 p-2 bg-gray-50 rounded">
                            {getLabelFromValue(teamStrengthOptions, tenant?.tenant?.team_strength)}
                          </div>
                        </div>

                        <div className="info-item">
                          <label className="font-semibold text-gray-700 block mb-1">Business Website</label>
                          <div className="text-gray-900 p-2 bg-gray-50 rounded break-words">
                            {tenant?.tenant?.website_url ? (
                              <a href={tenant.tenant.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                                {tenant.tenant.website_url}
                              </a>
                            ) : "N/A"}
                          </div>
                        </div>

                        <div className="info-item">
                          <label className="font-semibold text-gray-700 block mb-1">Social Media URL</label>
                          <div className="text-gray-900 p-2 bg-gray-50 rounded break-words">
                            {tenant?.tenant?.social_media_url ? (
                              <a href={tenant.tenant.social_media_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                                {tenant.tenant.social_media_url}
                              </a>
                            ) : "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Admin Information Block */}
                  <div className="bg-white box-shadow p-5 space-y-7 rounded-md">
                    <h2 className="text-h4 text-blue border-b pb-2">Admin Information</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Admin Name</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded">{tenant?.name || "N/A"}</div>
                      </div>

                      <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Email</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded break-words">{tenant?.email || "N/A"}</div>
                      </div>

                      {/* <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Assigned Staff</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded">
                          {getStaffName(tenant?.tenant?.assigned_staff_id)}
                        </div>
                      </div> */}

                      {/* <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Assigned Coupon</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded">
                          {getCouponName(tenant?.tenant?.subscription?.coupon_id)}
                        </div>
                      </div> */}
                    </div>
                  </div>

                  {/* Employee Statistics Block */}
                  <div className="bg-white box-shadow p-5 space-y-7 rounded-md">
                    <h2 className="text-h4 text-blue border-b pb-2">Employee Statistics</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="info-item text-center p-4 bg-blue-50 rounded-lg">
                        <label className="font-semibold text-gray-700 block mb-2">Total Employees</label>
                        <div className="text-3xl font-bold text-blue-700">{tenant?.total_employee || "0"}</div>
                      </div>

                      <div className="info-item text-center p-4 bg-green-50 rounded-lg">
                        <label className="font-semibold text-gray-700 block mb-2">Active Employees</label>
                        <div className="text-3xl font-bold text-green-700">{tenant?.active_employee || "0"}</div>
                      </div>

                      <div className="info-item text-center p-4 bg-red-50 rounded-lg">
                        <label className="font-semibold text-gray-700 block mb-2">Inactive Employees</label>
                        <div className="text-3xl font-bold text-red-700">{tenant?.inactive_employee || "0"}</div>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Details Block */}
                  <div className="bg-white box-shadow p-5 space-y-7 rounded-md">
                    <h2 className="text-h4 text-blue border-b pb-2">Subscription Details</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Current Plan</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded">
                          {getPlanName(tenant?.tenant?.subscription?.plan_id)}
                        </div>
                      </div>

                      <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Subscription Status</label>
                        <div className="p-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            tenant?.tenant?.subscription?.status === "active" 
                              ? "bg-green-100 text-green-800" 
                              : "bg-red-100 text-red-800"
                          }`}>
                            {tenant?.tenant?.subscription?.status || "N/A"}
                          </span>
                        </div>
                      </div>

                      <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Start Date</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded">
                          {displayDateAsDMY(tenant?.tenant?.subscription?.start_date) || "N/A"}
                        </div>
                      </div>

                      <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">End Date</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded">
                          {displayDateAsDMY(tenant?.tenant?.subscription?.end_date) || "N/A"}
                        </div>
                      </div>

                      {/* <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Employee Limit</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded">
                          {tenant?.tenant?.subscription?.max_active_users || "N/A"} Employees
                        </div>
                      </div>

                      <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Billing Cycle</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded capitalize">
                          {tenant?.tenant?.subscription?.billing_cycle || "N/A"}
                        </div>
                      </div>

                      <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Auto Renew</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded">
                          {tenant?.tenant?.subscription?.auto_renew ? "Yes" : "No"}
                        </div>
                      </div> */}

                      <div className="info-item">
                        <label className="font-semibold text-gray-700 block mb-1">Subscription ID</label>
                        <div className="text-gray-900 p-2 bg-gray-50 rounded">
                          {tenant?.tenant?.subscription?.id || "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Back Button */}
                  <div className="flex gap-4 mt-4">
                    <button
                      type="button"
                      className="skip-btn border-blue"
                      onClick={() => navigate(-1)}
                    >
                      Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ViewTenant;